import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  ApiResponse,
  GamePreferences,
  GameRoundHistory,
  GameRulesV2,
  InactivityConfig,
  UserRoundHistoryItems,
  UserRoundHistoryProps,
} from '../types';

export const gameApi = createApi({
  reducerPath: 'gameApi',
  baseQuery,
  tagTypes: [
    'GamePreferences',
    'GameHistory',
    'GameRulesV2',
    'RoundHistory',
    'InactivityConfig',
  ],
  endpoints: (builder) => ({
    getGamePreferences: builder.query<
      ApiResponse<GamePreferences>,
      { userId: number; gameId: string }
    >({
      query: ({ userId, gameId }) => ({
        url: `api/player/v1/user/${userId}/game/${gameId}/preferences`,
        method: 'GET',
      }),
      providesTags: ['GamePreferences'],
    }),
    saveGamePreferences: builder.mutation<
      ApiResponse<GamePreferences>,
      GamePreferences
    >({
      query: (body) => ({
        url: `api/player/v1/user/${body.userId}/game/${body.gameId}/preferences`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GamePreferences'],
    }),
    getGameHistory: builder.query<ApiResponse<GameRoundHistory>, string>({
      query: (gameId) => ({
        url: `api/player/v1/games/${gameId}/history`,
        method: 'GET',
      }),
      providesTags: ['GameHistory'],
    }),
    getGameRulesV2: builder.query<ApiResponse<GameRulesV2>, string>({
      query: (gameId) => ({
        url: `api/player/v2/games/${gameId}/rules`,
        method: 'GET',
      }),
      providesTags: ['GameRulesV2'],
    }),
    getUserRoundHistory: builder.query<
      ApiResponse<UserRoundHistoryItems>,
      UserRoundHistoryProps
    >({
      query: ({ userId, ...rest }) => ({
        url: `api/player/v2/user/${userId}/history`,
        method: 'GET',
        params: rest,
      }),
      providesTags: ['RoundHistory'],
    }),
    getGameInactivityConfig: builder.query<
      ApiResponse<InactivityConfig>,
      string
    >({
      query: (gameId) => ({
        url: `api/player/v1/game-view-configs/${gameId}`,
        method: 'GET',
      }),
      providesTags: ['InactivityConfig'],
    }),
  }),
});

export const {
  useGetGamePreferencesQuery,
  useSaveGamePreferencesMutation,
  useGetGameHistoryQuery,
  useGetGameRulesV2Query,
  useGetUserRoundHistoryQuery,
  useGetGameInactivityConfigQuery,
} = gameApi;
